import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  Typography,
  Button,
  Textarea,
  Input,
  IconButton,
  Tooltip,
} from '@material-tailwind/react';
import {
  ArrowPathIcon,
  PlusIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

/**
 * Dynamic sections editor for report sections that have a sections[] array
 * Supports: heading + content pairs with add/remove/reorder
 * Also handles special fields like introText, disclaimerText, brokerageFee, subtitle
 */
function DynamicSectionsEditor({
  title,
  sections = [],
  specialFields = {},
  specialFieldsConfig = [],
  onSave,
  onReset,
  isSaving,
}) {
  const [localSections, setLocalSections] = useState(sections);
  const [localSpecialFields, setLocalSpecialFields] = useState(specialFields);
  const [isDirty, setIsDirty] = useState(false);

  // Sync with props when they change externally
  useEffect(() => {
    setLocalSections(sections);
    setLocalSpecialFields(specialFields);
    setIsDirty(false);
  }, [sections, specialFields]);

  const handleSectionChange = (index, field, value) => {
    const updated = [...localSections];
    updated[index] = { ...updated[index], [field]: value };
    setLocalSections(updated);
    setIsDirty(true);
  };

  const handleSpecialFieldChange = (fieldName, value) => {
    setLocalSpecialFields((prev) => ({ ...prev, [fieldName]: value }));
    setIsDirty(true);
  };

  const addSection = () => {
    setLocalSections([...localSections, { heading: '', content: '' }]);
    setIsDirty(true);
  };

  const removeSection = (index) => {
    const updated = localSections.filter((_, i) => i !== index);
    setLocalSections(updated);
    setIsDirty(true);
  };

  const moveSection = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= localSections.length) return;

    const updated = [...localSections];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setLocalSections(updated);
    setIsDirty(true);
  };

  const handleSave = () => {
    const content = {
      ...localSpecialFields,
      sections: localSections,
    };
    onSave(content);
    setIsDirty(false);
  };

  const handleReset = () => {
    onReset();
    setIsDirty(false);
  };

  return (
    <Card className="h-full flex flex-col">
      <CardBody className="flex-1 flex flex-col p-4 overflow-hidden">
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <Typography variant="h6" color="blue-gray">
            {title}
          </Typography>
          <div className="flex gap-2">
            <Tooltip content="Reset to default">
              <IconButton variant="text" size="sm" onClick={handleReset}>
                <ArrowPathIcon className="h-4 w-4" />
              </IconButton>
            </Tooltip>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Special fields at the top */}
          {specialFieldsConfig.map((field) => (
            <div key={field.name} className="space-y-1">
              <Typography variant="small" className="font-medium text-gray-700">
                {field.label}
              </Typography>
              {field.type === 'textarea' ? (
                <Textarea
                  value={localSpecialFields[field.name] || ''}
                  onChange={(e) => handleSpecialFieldChange(field.name, e.target.value)}
                  rows={field.rows || 3}
                  className="w-full"
                />
              ) : (
                <Input
                  value={localSpecialFields[field.name] || ''}
                  onChange={(e) => handleSpecialFieldChange(field.name, e.target.value)}
                  className="w-full"
                />
              )}
            </div>
          ))}

          {/* Dynamic sections */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Typography variant="small" className="font-medium text-gray-700">
                Sections
              </Typography>
              <Button
                variant="text"
                size="sm"
                className="flex items-center gap-1"
                onClick={addSection}
              >
                <PlusIcon className="h-4 w-4" />
                Add Section
              </Button>
            </div>

            {localSections.map((section, index) => (
              <div
                key={index}
                className="border rounded-lg p-3 space-y-2 bg-gray-50"
              >
                <div className="flex items-center justify-between">
                  <Typography variant="small" className="font-medium text-gray-600">
                    Section {index + 1}
                  </Typography>
                  <div className="flex items-center gap-1">
                    <Tooltip content="Move up">
                      <IconButton
                        variant="text"
                        size="sm"
                        onClick={() => moveSection(index, -1)}
                        disabled={index === 0}
                      >
                        <ChevronUpIcon className="h-4 w-4" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip content="Move down">
                      <IconButton
                        variant="text"
                        size="sm"
                        onClick={() => moveSection(index, 1)}
                        disabled={index === localSections.length - 1}
                      >
                        <ChevronDownIcon className="h-4 w-4" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip content="Remove section">
                      <IconButton
                        variant="text"
                        size="sm"
                        color="red"
                        onClick={() => removeSection(index)}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
                <Input
                  label="Heading"
                  value={section.heading || ''}
                  onChange={(e) => handleSectionChange(index, 'heading', e.target.value)}
                />
                <Textarea
                  label="Content"
                  value={section.content || ''}
                  onChange={(e) => handleSectionChange(index, 'content', e.target.value)}
                  rows={4}
                />
              </div>
            ))}

            {localSections.length === 0 && (
              <div className="text-center py-4 text-gray-500 border rounded-lg border-dashed">
                <Typography variant="small">No sections yet. Click "Add Section" to create one.</Typography>
              </div>
            )}
          </div>
        </div>

        {/* Save button */}
        <div className="mt-4 flex-shrink-0">
          <Button
            onClick={handleSave}
            disabled={!isDirty || isSaving}
            loading={isSaving}
            fullWidth
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

export default DynamicSectionsEditor;
